<template>
  <v-card flat>
    <v-tabs v-model="activeTab">
      <v-tab>Meals</v-tab>
      <v-tab>Categories</v-tab>
      <v-tab>Reports</v-tab>
    </v-tabs>
    <div v-show="activeTab === 1">
      <MenuCategoriesEdit
        :available-tags="tags"
        :available-menu-category-keys="categories"
        :menu="menu"
      />
    </div>
    <div v-show="activeTab === 2">
      <MenuSalesReport :menu="menu"/>
    </div>
    <div v-show="activeTab === 0">
    <v-dialog v-model="showDialog" persistent max-width="900px">
      <MenuEditDialog
        :item="editItem"
        :categories="categories"
        :tags="tags"
        v-on:close="showDialog=false"
        v-on:save="saveItem"
        v-on:delete="deleteItem"
        v-on:add-tag="addTag"
        v-on:add-category="addCategory"
      />
    </v-dialog>

    <v-dialog v-model="showBulkContainerDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>Set Container Size by Category</v-card-title>
        <v-card-text>
          <v-form>
            <v-select
              v-model="bulkContainerCategory"
              :items="categories"
              label="Category"
              required
            />
            <v-text-field
              v-model="bulkContainerSize"
              type="number"
              min="0"
              label="Container Size"
              clearable
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined @click="showBulkContainerDialog=false">Cancel</v-btn>
          <v-btn outlined color="primary" @click="setBulkContainerSize">Set Container Size</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBulkPrintOptionDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>Set Print Option by Category</v-card-title>
        <v-card-text>
          <v-form>
            <v-select
              v-model="bulkPrintOptionCategory"
              :items="categories"
              label="Category"
              required
            />
            <v-select
              v-model="bulkPrintOption"
              :items="['sleeve', 'sticker', 'none']"
              label="Print Option"
              required
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined @click="showBulkPrintOptionDialog=false">Cancel</v-btn>
          <v-btn outlined color="primary" @click="setBulkPrintOption">Set Print Option</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showProgressDialog" persistent max-width="500px">
      <v-card>
        <v-card-title>{{ progressTitle }}</v-card-title>
        <v-card-text>
          <div class="mb-4">{{ progressMessage }}</div>
          <v-progress-linear
            :value="progressPercent"
            height="25"
            color="primary"
          >
            <template v-slot:default>
              <strong>{{ progressCurrent }} / {{ progressTotal }}</strong>
            </template>
          </v-progress-linear>
        </v-card-text>
      </v-card>

    </v-dialog>

    <v-card-title>
      Edit Menu
      <v-btn
        v-if="uncategorizedCount > 0"
        color="error"
        :outlined="!showOnlyUncategorized"
        small
        class="ml-4"
        @click="showOnlyUncategorized = !showOnlyUncategorized"
      >
        <v-icon left small>mdi-alert</v-icon>
        {{ uncategorizedCount }} without category
        <span v-if="showOnlyUncategorized" class="ml-1">(show all)</span>
      </v-btn>
      <v-spacer/>
      <v-btn outlined @click="showAdvancedOptions = !showAdvancedOptions">
        {{ showAdvancedOptions ? 'Hide' : 'Show' }} Advanced Options
      </v-btn>
      <v-spacer/>
      <v-btn v-if="showAdvancedOptions" outlined @click="showBulkContainerDialog=true">Set Container Size by Category
      </v-btn>
      <v-spacer v-if="showAdvancedOptions"/>
      <v-btn v-if="showAdvancedOptions" outlined @click="showBulkPrintOptionDialog=true">Set Print Option by Category
      </v-btn>
      <v-spacer v-if="showAdvancedOptions"/>
      <v-btn v-if="showAdvancedOptions" outlined @click="saveAll" :loading="savingAll" :disabled="savingAll">Save All
      </v-btn>
      <v-spacer/>
      <v-btn outlined @click="openMultiTagDialog">Multi-tag</v-btn>
      <v-spacer/>
      <v-btn outlined @click="editRow()">New</v-btn>
    </v-card-title>

    <v-dialog v-model="multiTagDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <template v-if="!activeMultiTag">Choose a tag for multi-tagging</template>
          <template v-else>
            <div class="d-flex align-center" style="width:100%;">
              <span>Multi-tag:</span>
              <v-chip small color="primary" class="ml-2">{{ activeMultiTag }}</v-chip>
              <v-spacer/>
              <v-chip small outlined color="success">{{ multiTaggedCount }} tagged</v-chip>
            </div>
          </template>
        </v-card-title>
        <v-card-text>
          <template v-if="!activeMultiTag">
            <v-select
              v-model="multiTagInput"
              :items="sortedTags"
              label="Select a tag"
              clearable
              autofocus
            />
            <div class="d-flex align-center mt-2">
              <span style="color: #666; font-size: 13px;">Or</span>
              <v-btn small text color="primary" @click="promptNewTag" class="ml-1">Create New Tag</v-btn>
            </div>
          </template>
          <template v-else>
            <v-text-field
              v-model="multiTagSearch"
              label="Search meals"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              hide-details
              class="mb-3"
            />
            <div style="max-height: 400px; overflow-y: auto;">
              <v-list dense>
                <v-list-item
                  v-for="meal in multiTagFilteredMeals"
                  :key="meal.key"
                  @click="toggleMultiTag(meal)"
                  :disabled="multiTagSaving === meal.key"
                >
                  <v-list-item-action class="mr-3">
                    <v-checkbox
                      :input-value="mealHasActiveMultiTag(meal)"
                      @click.stop="toggleMultiTag(meal)"
                      :disabled="multiTagSaving === meal.key"
                      dense
                      hide-details
                      class="ma-0 pa-0"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ meal.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ meal.category }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="multiTagSaving === meal.key">
                    <v-progress-circular indeterminate size="20" width="2" color="primary"/>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <template v-if="!activeMultiTag">
            <v-btn outlined @click="multiTagDialog = false">Cancel</v-btn>
            <v-btn outlined color="primary" :disabled="!multiTagInput" @click="startMultiTagMode">Next</v-btn>
          </template>
          <template v-else>
            <v-btn outlined @click="activeMultiTag = null">Back</v-btn>
            <v-btn outlined color="primary" @click="closeMultiTagDialog">Done</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card-actions>
      <DebouncedTextField
        clearable
        v-model="search"
        label="Search"
        prepend-inner-icon="mdi-magnify"
      />
      <v-spacer/>
      <v-checkbox
        v-model="hideExpiredMeals"
        label="Hide Expired Meals"
        dense
        hide-details
      />
    </v-card-actions>
    <v-card-text>
      <v-data-table
        :search="search"
        :items="displayedMeals"
        :headers="headers"
        hide-default-footer
        disable-pagination
        item-key="key"
        dense
        :item-class="getRowClass"
        :sort-by="['featured', 'category', 'index', 'name']"
        :sort-desc="[true, false, false]"
        multi-sort
      >
        <template v-slot:item.editAction="{item}">
          <v-btn outlined small @click.stop="editRow(item)">
            <v-icon left small>mdi-pencil</v-icon>
            Edit
          </v-btn>
        </template>
        <template v-slot:item.featured="{item}">
          <v-checkbox
            :input-value="item.tags && item.tags.includes('featured')"
            @click.stop="toggleTag(item, 'featured')"
            dense
            hide-details
            class="ma-0 pa-0"
          />
        </template>
        <template v-slot:item.menuCategoriesForMeal="{item}">
          <div v-if="menuCategoriesForMeal(item).length === 0" style="color:#bbb;font-size:12px;">
            <em>—</em>
          </div>
          <div v-else style="display:flex;flex-wrap:wrap;gap:4px;">
            <v-chip
              v-for="c in menuCategoriesForMeal(item)"
              :key="'mc-'+item.key+'-'+c.id"
              x-small outlined color="primary"
            >{{ c.name }}</v-chip>
          </div>
        </template>
        <template v-slot:item.name="{item}">
          <div>
            <div style="font-weight: 500;">{{ item.name }}</div>
            <div style="font-size: 11px; color: #666; margin-top: 2px; display: flex; flex-wrap: wrap; gap: 4px 12px;">
              <span><strong>ID:</strong> {{ item.id }}</span>
              <span><strong>Price:</strong> ${{ (item.price / 100).toFixed(2) }}</span>
              <span v-if="item.availableFrom && !isDateInPast(item.availableFrom)">
                <strong>Deliveries from:</strong> {{ item.availableFrom }}
              </span>
              <span v-if="item.availableTo" :style="isExpiringSoon(item) ? 'color: #e65100; font-weight: 600;' : ''">
                <strong>Last delivery:</strong> {{ item.availableTo }}
              </span>
              <span v-if="item.regionText"><strong>Regions:</strong> {{ item.regionText }}</span>
            </div>
          </div>
        </template>
        <template v-slot:item.mealDetails="{item}">
          <div style="display: flex; flex-direction: column; gap: 8px; padding: 8px 0;">
            <!-- Row 1: Portions, Size, Print -->
            <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="font-size: 12px; color: #666; font-weight: 500;">Portions:</span>
                <v-chip small outlined color="primary">{{ item.portions }}</v-chip>
              </div>
              <div style="display: flex; align-items: center; gap: 6px;">
                <template v-if="Number.isFinite(item.containerSize)">
                  <span style="font-size: 12px; color: #666; font-weight: 500;">Container Size:</span>
                  <v-chip small outlined color="primary">
                    {{item.containerSize}}
                  </v-chip>
                </template>
                <template v-else>
                  <span style="font-size: 12px; color: red; font-weight: 500;">
                    Do Not Pack in Box
                  </span>

                </template>

              </div>
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="font-size: 12px; color: #666; font-weight: 500;">Print:</span>
                <v-chip
                  small
                  :color="item.printOption === 'none' ? 'grey' : item.printOption === 'sticker' ? 'blue' : item.printOption === 'sleeve' ? 'green' : 'grey'"
                  :dark="!!item.printOption"
                  :outlined="!item.printOption"
                >
                  {{ item.printOption || 'not set' }}
                </v-chip>
              </div>
            </div>
            <!-- Row 2: Tags -->
            <div v-if="item.tags && item.tags.length"
                 style="display: flex; gap: 6px; flex-wrap: wrap; align-items: center;">
              <span style="font-size: 12px; color: #666; font-weight: 500;">Tags:</span>
              <v-chip
                v-for="(tag,index) of item.tags"
                v-bind:key="index"
                outlined
                small
                color="orange">
                {{ tag }}
              </v-chip>
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      color="success"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    </div>
  </v-card>
</template>

<script>
import api from "@/api";
import MenuEditDialog from "@/components/MenuEditDialog";
import MenuCategoriesEdit from "@/views/MenuCategoriesEdit.vue";
import MenuSalesReport from "@/views/MenuSalesReport.vue";
import DebouncedTextField from "@/components/DebouncedTextField.vue";
import {getNextOrderingDate} from "../store/utils";
import moment from "moment";

export default {
  name: "MenuEdit",
  components: {MenuEditDialog, MenuCategoriesEdit, MenuSalesReport, DebouncedTextField},
  data() {
    return {
      activeTab: 0,
      menu: [],
      categories: [],
      tags: [],
      menuCategories: [],
      showDialog: false,
      editItem: undefined,
      editMeal: null,
      search: null,
      snackbar: false,
      snackbarText: '',
      showBulkContainerDialog: false,
      bulkContainerCategory: '',
      bulkContainerSize: null,
      showBulkPrintOptionDialog: false,
      bulkPrintOptionCategory: '',
      bulkPrintOption: null,
      savingAll: false,
      showAdvancedOptions: false,
      showOnlyUncategorized: false,
      hideExpiredMeals: true,
      showProgressDialog: false,
      progressTitle: '',
      progressMessage: '',
      progressCurrent: 0,
      progressTotal: 0,
      multiTagDialog: false,
      multiTagInput: null,
      activeMultiTag: null,
      multiTagSearch: '',
      multiTagSaving: null
    }
  },
  mounted() {
    this.fetchMenuCategories();
    return this.fetchMenu();
  },
  methods: {
    fetchMenu() {
      api.get('v2/menu/static', {params: {includePrismaData: true}})
        .then(({data}) => {
          this.updateMenu(data);
        })
    },
    fetchMenuCategories() {
      api.get('v2/menu_categories/draft')
        .then(({data}) => {
          this.menuCategories = Array.isArray(data) ? data : [];
        })
        .catch(e => console.error('failed to load menu categories', e));
    },
    menuCategoryMatches(meal, criteria) {
      if (!criteria) return true;
      const {categories, tagsAny, tagsAll, tagsNone} = criteria;
      if (Array.isArray(categories) && categories.length > 0) {
        if (!categories.includes(meal.category)) return false;
      }
      const mealTags = Array.isArray(meal.tags) ? meal.tags : [];
      if (Array.isArray(tagsAny) && tagsAny.length > 0 && !tagsAny.some(t => mealTags.includes(t))) return false;
      if (Array.isArray(tagsAll) && tagsAll.length > 0 && !tagsAll.every(t => mealTags.includes(t))) return false;
      if (Array.isArray(tagsNone) && tagsNone.length > 0 && tagsNone.some(t => mealTags.includes(t))) return false;
      return true;
    },
    menuCategoriesForMeal(meal) {
      if (!meal || !this.menuCategories.length) return [];
      return this.menuCategories.filter(c => this.menuCategoryMatches(meal, c.criteria));
    },
    updateMenu(data) {
      console.log('got menu', data);
      const menu = [];
      const categories = Object.keys(data);
      categories.forEach(category => {
        const meals = data[category];
        meals.forEach(meal => {
          menu.push({
            tags: [],
            ...meal,
            category,
            originalCategory: category,
            portions: meal.portions,
            containerSize: meal.containerSize,
          });
        });
      });
      this.menu = menu;
      this.categories = categories;
      this.tags = [...new Set(this.menu.flatMap(item => item.tags || []))];
    },
    editRow(item) {
      if (!item) {
        // new item, set some defaults
        item = {
          containerSize: 1,
          portions: 1,
          printOption: 'sleeve'
        }
      }
      this.$nextTick(() => {
        this.editItem = {...item};
        this.editMeal = {
          name: item.name,
          id: Number(item.id)
        }
        this.showDialog = true;
      });
    },
    async saveItem(item) {
      console.log('saving', item);
      const payload = {
        ...item
      };
      const {category, originalCategory} = item;
      delete payload.originalCategory;
      delete payload.key;
      if (originalCategory && category !== originalCategory) {
        await this.deleteItem({...item, category: originalCategory});
      }
      return api.put('v2/menu/static', payload)
        .then(r => {
          console.log('success', r);
          if (!this.savingAll) {
            this.snackbarText = 'Menu item saved successfully!';
            this.snackbar = true;
          }
        })
        .catch(e => {
          console.error('fail', e);
          if (!this.savingAll) {
            this.snackbarText = 'Error saving menu item';
            this.snackbar = true;
          }
        })
        .finally(() => {
          this.fetchMenu();
        })
    },
    deleteItem(item) {
      console.log('deleting', item);
      const payload = {
        ...item
      };
      return api.put('v2/menu/static', payload, {params: {deleteItem: true}})
        .then(r => {
          console.log('success', r);
          this.updateMenu(r.data);
        })
        .catch(e => console.error('fail', e))
    },
    addTag(tag) {
      this.tags.push(tag);
    },
    addCategory(category) {
      if (!this.categories.includes(category)) {
        this.categories.push(category);
      }
    },
    formatRegion(region) {
      if (!region) return '';

      // Handle both object format and string format
      if (typeof region === 'string') {
        const parts = region.split(':');
        return `${parts[0]}, ${parts[1]}, ${parts[2]}`;
      }

      // Otherwise assume it's an object with our expected structure
      return `${region.area}, ${region.city}, ${region.province}`;
    },
    async setBulkContainerSize() {
      if (!this.bulkContainerCategory || this.bulkContainerSize === null) {
        this.snackbarText = 'Please select a category and enter a container size';
        this.snackbar = true;
        return;
      }

      const itemsToUpdate = this.menu.filter(item => item.category === this.bulkContainerCategory);

      if (itemsToUpdate.length === 0) {
        this.snackbarText = 'No items found in the selected category';
        this.snackbar = true;
        return;
      }

      // Close the input dialog and show progress dialog
      this.showBulkContainerDialog = false;
      this.progressTitle = 'Updating Container Sizes';
      this.progressMessage = `Setting container size to ${this.bulkContainerSize} for items in category "${this.bulkContainerCategory}"`;
      this.progressCurrent = 0;
      this.progressTotal = itemsToUpdate.length;
      this.showProgressDialog = true;

      try {
        for (let i = 0; i < itemsToUpdate.length; i++) {
          const item = itemsToUpdate[i];
          const updatedItem = {...item, containerSize: this.bulkContainerSize};
          await this.saveItem(updatedItem);
          this.progressCurrent = i + 1;
        }

        this.showProgressDialog = false;
        this.snackbarText = `Updated container size for ${itemsToUpdate.length} items in category "${this.bulkContainerCategory}"`;
        this.snackbar = true;
        this.bulkContainerCategory = '';
        this.bulkContainerSize = null;
      } catch (error) {
        console.error('Error updating container sizes:', error);
        this.showProgressDialog = false;
        this.snackbarText = 'Error updating container sizes';
        this.snackbar = true;
      }
    },
    async setBulkPrintOption() {
      if (!this.bulkPrintOptionCategory || !this.bulkPrintOption) {
        this.snackbarText = 'Please select a category and a print option';
        this.snackbar = true;
        return;
      }

      const itemsToUpdate = this.menu.filter(item => item.category === this.bulkPrintOptionCategory);

      if (itemsToUpdate.length === 0) {
        this.snackbarText = 'No items found in the selected category';
        this.snackbar = true;
        return;
      }

      // Close the input dialog and show progress dialog
      this.showBulkPrintOptionDialog = false;
      this.progressTitle = 'Updating Print Options';
      this.progressMessage = `Setting print option to "${this.bulkPrintOption}" for items in category "${this.bulkPrintOptionCategory}"`;
      this.progressCurrent = 0;
      this.progressTotal = itemsToUpdate.length;
      this.showProgressDialog = true;

      try {
        for (let i = 0; i < itemsToUpdate.length; i++) {
          const item = itemsToUpdate[i];
          const updatedItem = {...item, printOption: this.bulkPrintOption};
          await this.saveItem(updatedItem);
          this.progressCurrent = i + 1;
        }

        this.showProgressDialog = false;
        this.snackbarText = `Updated print option for ${itemsToUpdate.length} items in category "${this.bulkPrintOptionCategory}"`;
        this.snackbar = true;
        this.bulkPrintOptionCategory = '';
        this.bulkPrintOption = null;
      } catch (error) {
        console.error('Error updating print options:', error);
        this.showProgressDialog = false;
        this.snackbarText = 'Error updating print options';
        this.snackbar = true;
      }
    },
    async saveAll() {
      this.savingAll = true;
      try {
        const itemsToSave = [...this.menuConverted];
        let savedCount = 0;
        let errorCount = 0;


        for (const item of itemsToSave) {
          try {
            await this.saveItem(item);
            savedCount++;
            this.snackbarText = `Saved ${savedCount} of ${itemsToSave.length} items`;
            this.snackbar = true;

          } catch (error) {
            console.error('Error saving item:', item.id, error);
            errorCount++;
          }
        }

        if (errorCount > 0) {
          this.snackbarText = `Saved ${savedCount} items, ${errorCount} failed`;
        } else {
          this.snackbarText = `Successfully saved all ${savedCount} items`;
        }
        this.snackbar = true;
      } catch (error) {
        console.error('Error in saveAll:', error);
        this.snackbarText = 'Error saving items';
        this.snackbar = true;
      } finally {
        this.savingAll = false;
      }
    },
    getRowClass(item) {
      // Check if the item has an availableTo date and if it's in the past
      if (item.availableTo) {
        const toDate = moment(item.availableTo);
        const today = moment(getNextOrderingDate());

        if (toDate < today) {
          return 'expired-availability';
        }
      }
      return '';
    },
    isItemExpired(item) {
      if (item.availableTo) {
        const toDate = moment(item.availableTo);
        const today = moment(getNextOrderingDate());
        return toDate < today;
      }
      return false;
    },
    isDateInPast(dateStr) {
      if (!dateStr) return false;
      return moment(dateStr).isBefore(moment(getNextOrderingDate()));
    },
    isExpiringSoon(item) {
      if (!item.availableTo) return false;
      const toDate = moment(item.availableTo);
      const today = moment(getNextOrderingDate());
      const oneMonthFromNow = moment(getNextOrderingDate()).add(1, 'month');
      return toDate.isSameOrAfter(today) && toDate.isSameOrBefore(oneMonthFromNow);
    },
    openMultiTagDialog() {
      this.multiTagInput = null;
      this.activeMultiTag = null;
      this.multiTagSearch = '';
      this.multiTagSaving = null;
      this.multiTagDialog = true;
    },
    startMultiTagMode() {
      const tag = (typeof this.multiTagInput === 'string' ? this.multiTagInput : this.multiTagInput?.value || '').trim();
      if (!tag) return;
      if (!this.tags.includes(tag)) this.tags.push(tag);
      this.activeMultiTag = tag;
    },
    promptNewTag() {
      const tag = prompt('Enter new tag name:');
      if (!tag || !tag.trim()) return;
      const trimmed = tag.trim();
      if (!this.tags.includes(trimmed)) this.tags.push(trimmed);
      this.multiTagInput = trimmed;
      this.activeMultiTag = trimmed;
    },
    closeMultiTagDialog() {
      this.multiTagDialog = false;
      this.activeMultiTag = null;
      this.multiTagSearch = '';
      this.multiTagSaving = null;
    },
    mealHasActiveMultiTag(meal) {
      return Array.isArray(meal.tags) && meal.tags.includes(this.activeMultiTag);
    },
    async toggleMultiTag(meal) {
      if (this.multiTagSaving) return;
      this.multiTagSaving = meal.key;
      try {
        await this.toggleTag(meal, this.activeMultiTag);
      } finally {
        this.multiTagSaving = null;
      }
    },
    async toggleTag(item, tag) {
      if (!tag) return;
      const existing = Array.isArray(item.tags) ? [...item.tags] : [];
      const idx = existing.indexOf(tag);
      if (idx >= 0) existing.splice(idx, 1);
      else existing.push(tag);
      await this.updateTags(item, existing);
    },
    async updateTags(item, newTags) {
      const cleaned = (Array.isArray(newTags) ? newTags : [])
        .map(t => (typeof t === 'string' ? t.trim() : ''))
        .filter(t => t.length > 0);
      cleaned.forEach(t => {
        if (!this.tags.includes(t)) this.tags.push(t);
      });
      const updatedItem = {...item, tags: cleaned};
      await this.saveItem(updatedItem);
    },
    async toggleFeatured(item) {
      // Create a copy of the item with updated tags
      const updatedItem = {...item};
      if (!updatedItem.tags) {
        updatedItem.tags = [];
      }

      const featuredIndex = updatedItem.tags.indexOf('featured');
      if (featuredIndex > -1) {
        // Remove featured tag
        updatedItem.tags = updatedItem.tags.filter(tag => tag !== 'featured');
      } else {
        // Add featured tag
        updatedItem.tags = [...updatedItem.tags, 'featured'];
      }

      // Save the item
      await this.saveItem(updatedItem);
    }
  },
  computed: {
    menuConverted() {
      let items = this.menu.map(item => ({
        ...item,
        key: `${item.id}-${item.category}`,
        regionText: (item.regions || []).map(this.formatRegion).join(', '),
        featured: item.tags && item.tags.includes('featured') ? 1 : 0 // Add sortable featured property
      }));

      // Filter out expired meals if hideExpiredMeals is true
      if (this.hideExpiredMeals) {
        items = items.filter(item => !this.isItemExpired(item));
      }

      return items;
    },
    uncategorizedCount() {
      return this.menuConverted.filter(item => this.menuCategoriesForMeal(item).length === 0).length;
    },
    displayedMeals() {
      if (this.showOnlyUncategorized && this.uncategorizedCount > 0) {
        return this.menuConverted.filter(item => this.menuCategoriesForMeal(item).length === 0);
      }
      return this.menuConverted;
    },
    sortedTags() {
      return [...this.tags].sort((a, b) => a.localeCompare(b));
    },
    multiTaggedCount() {
      if (!this.activeMultiTag) return 0;
      return this.menuConverted.filter(m => Array.isArray(m.tags) && m.tags.includes(this.activeMultiTag)).length;
    },
    multiTagFilteredMeals() {
      const search = (this.multiTagSearch || '').toLowerCase().trim();
      let items = this.menuConverted;
      if (search) {
        items = items.filter(m => {
          if (m.name && m.name.toLowerCase().includes(search)) return true;
          if (m.category && m.category.toLowerCase().includes(search)) return true;
          const cats = this.menuCategoriesForMeal(m);
          if (cats.some(c => c.name && c.name.toLowerCase().includes(search))) return true;
          return false;
        });
      }
      return items;
    },
    progressPercent() {
      if (this.progressTotal === 0) return 0;
      return (this.progressCurrent / this.progressTotal) * 100;
    },
    headers() {
      return [
        {"value": "featured", "text": "Featured", "width": "80px"},
        {"value": "index", "text": "Idx", "width": "50px"},
        {"value": "name", "text": "Name", "width": "350px"},
        {"value": "category", "text": "Meal Type", "width": "110px"},
        {"value": "menuCategoriesForMeal", "text": "Categories", "sortable": false, "width": "260px"},
        {"value": "mealDetails", "text": "Details", "sortable": false, "width": "350px"},
        {"value": "editAction", "text": "", "sortable": false, "width": "90px", "align": "end"}
      ];
    }
  }
}
</script>

<style scoped>
::v-deep .expired-availability {
  background-color: #ffebee !important;
}

::v-deep .expired-availability:hover {
  background-color: #ffcdd2 !important;
}
</style>